import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import rg4js from "raygun4js";
import { useEffect, useRef, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
  ArrowRight,
  ExclamationTriangleFill,
  PencilFill,
  X,
} from "react-bootstrap-icons";
import {
  CommunicationLanguage,
  type Interrogatory,
  ObjectTypeEnum,
  type ViewCommunicationItem,
} from "@/briefpoint-client";
import { useCommunicationApi } from "@/hooks/useApi";
import { COLLAPSED_ASSOCIATED_OBJECTS_TO_SHOW } from "@/lib/constants";
import { getCommunicationsForObjectOptions } from "@/lib/query-options";
import { normalizeError } from "@/lib/utils";
import Button from "../Button";
import HelpTooltip from "../HelpTooltip";
import { toast } from "../ui/toast";
import styles from "./ReviewClientTaskBox.module.scss";

interface Props {
  communication: ViewCommunicationItem;
  interrogatories: Interrogatory[];
  setDeleteCommunication: (communication: ViewCommunicationItem) => void;
  setEditCommunication: (communication: ViewCommunicationItem) => void;
  translatedCommunication?: ViewCommunicationItem;
  language: CommunicationLanguage;
  documentId: string;
}

export default function ReviewClientTaskBox(props: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedCommunication, setEditedCommunication] = useState("");

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const communicationApi = useCommunicationApi();

  const editTaskMutation = useMutation({
    mutationFn: async () => {
      return communicationApi.communicationPutCommunicationItem({
        id: props.translatedCommunication?.id ?? "",
        putCommunicationItem: {
          ...props.translatedCommunication,
          communication: editedCommunication,
        },
      });
    },
    onSuccess: () => {
      toast.success("Task updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update task. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      setEditMode(false);
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          props.documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
    },
  });

  function findInterrogatory(interrogatoryId: string) {
    return (
      props.interrogatories?.find(
        (interrogatory) => interrogatory.id.toString() === interrogatoryId,
      ) ?? null
    );
  }

  const unassigned =
    !props.communication.responseInfo?.associatedObjectIds?.length;

  useEffect(() => {
    if (editMode) {
      if (textareaRef.current) {
        //focus the textarea and move the cursor to the end of the text
        textareaRef.current.focus();
        textareaRef.current.selectionStart = textareaRef.current.value.length;
      }
    }
  }, [editMode]);

  useEffect(() => {
    setEditedCommunication(props.translatedCommunication?.communication ?? "");
  }, [props.translatedCommunication?.communication]);

  return (
    <div className={styles.communicationContainer}>
      <div className={styles.communicationItem}>
        <div
          className={clsx(
            styles.clientTaskContainer,
            props.language === CommunicationLanguage.ES &&
              !unassigned &&
              styles.shortClientTaskContainer,
          )}
        >
          <div
            className={clsx(
              styles.communicationTitle,
              unassigned && styles.unassignedTask,
            )}
          >
            <div className="fw-bold">Client Task</div>
            {unassigned && (
              <div className="d-flex gap-2 align-items-center">
                <ExclamationTriangleFill
                  size={16}
                  style={{ color: "var(--feedback-warning-dark)" }}
                />
                <span className="fs-7">Not visible to your client</span>
              </div>
            )}
          </div>
          <div className={styles.communicationContent}>
            <div className={styles.communicationContentText}>
              {props.communication.communication}
            </div>
            <div className={styles.communicationAction}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  props.setDeleteCommunication(props.communication)
                }
                className={styles.communicationActionButton}
              >
                <X style={{ scale: 1.5 }} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => props.setEditCommunication(props.communication)}
                className={styles.communicationActionButton}
              >
                <PencilFill style={{ width: 12, height: 12 }} />
              </Button>
            </div>
          </div>
        </div>
        {props.language === CommunicationLanguage.ES && !unassigned && (
          <>
            <ArrowRight size={16} />
            <div
              className={clsx(
                styles.clientTaskContainer,
                styles.shortClientTaskContainer,
              )}
            >
              <div
                className={clsx(
                  styles.communicationTitle,
                  unassigned && styles.unassignedTask,
                )}
              >
                <div className="fw-bold d-flex gap-2 align-items-center">
                  Spanish Translation{" "}
                  <HelpTooltip text="Changing the Spanish translation will disable the translation feature for this item. " />
                </div>
                {unassigned && (
                  <div className="d-flex gap-2 align-items-center">
                    <ExclamationTriangleFill
                      size={16}
                      style={{ color: "var(--feedback-warning-dark)" }}
                    />
                    <span className="fs-7">Not visible to your client</span>
                  </div>
                )}
              </div>
              <div
                className={clsx(
                  styles.communicationContent,
                  !props.translatedCommunication &&
                    styles.translatingCommunicationContent,
                )}
              >
                <div className={styles.communicationContentText}>
                  {!props.translatedCommunication ? (
                    <div className="d-flex gap-2 align-items-center">
                      <Spinner
                        style={{ color: "var(--primary-2)", opacity: 0.5 }}
                        size="sm"
                        animation="border"
                      />{" "}
                      Translating...
                    </div>
                  ) : (
                    <div>
                      {editMode ? (
                        <Form.Control
                          as="textarea"
                          placeholder="Leave a comment here"
                          className={styles.editTextarea}
                          value={editedCommunication}
                          disabled={editTaskMutation.isPending}
                          onChange={(e) =>
                            setEditedCommunication(e.target.value ?? "")
                          }
                          onBlur={() => {
                            editTaskMutation.mutate();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              editTaskMutation.mutate();
                            }
                          }}
                          ref={textareaRef}
                        />
                      ) : (
                        <div>{editedCommunication}</div>
                      )}
                    </div>
                  )}
                </div>
                {!editMode && (
                  <div
                    className={clsx(
                      styles.communicationAction,
                      styles.spanishCommunicationAction,
                    )}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditMode(true);
                        setEditedCommunication(
                          props.translatedCommunication?.communication ?? "",
                        );
                      }}
                      className={styles.communicationActionButton}
                    >
                      <PencilFill style={{ width: 12, height: 12 }} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className={styles.communicationAssociatedContainer}>
        <div className={styles.curveGraphicContainer}>
          {/* add animation to the curve graphic */}
          {props.communication.responseInfo?.associatedObjectIds
            ?.slice(
              0,
              isExpanded ? undefined : COLLAPSED_ASSOCIATED_OBJECTS_TO_SHOW,
            )
            .map((_, index) => (
              <div
                key={index}
                className={styles.curveGraphic}
                style={{ top: index * 59 }}
              />
            ))}
        </div>
        {(props.communication.responseInfo?.associatedObjectIds?.length || 0) >
          0 && (
          <div className={styles.clientUploadsContainer}>
            <div className={styles.clientUploadsTitle}>
              Client uploads will be applied to:
            </div>

            {props.communication.responseInfo?.associatedObjectIds
              ?.sort((a, b) => Number(a) - Number(b))
              .slice(
                0,
                isExpanded ? undefined : COLLAPSED_ASSOCIATED_OBJECTS_TO_SHOW,
              )
              .map((objectId, index) => (
                <div className={styles.clientUploadsContent} key={index}>
                  <div className={styles.clientUploadsContentText}>
                    <b>No. {findInterrogatory(objectId)?.number}: </b>
                    {findInterrogatory(objectId)?.text}
                  </div>
                </div>
              ))}
            {(props.communication.responseInfo?.associatedObjectIds?.length ??
              0) > COLLAPSED_ASSOCIATED_OBJECTS_TO_SHOW && (
              <div className="d-flex gap-2 p-2 ps-3 align-items-center">
                {!isExpanded && (
                  <div className="fw-bold">
                    +{" "}
                    {(props.communication.responseInfo?.associatedObjectIds
                      ?.length ?? 0) -
                      COLLAPSED_ASSOCIATED_OBJECTS_TO_SHOW}{" "}
                    more
                  </div>
                )}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? "Show Less" : "Show All"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
